import { useEffect, useState } from 'react'
import { cocinaApi } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'

interface ComidaCola {
  id: number
  cliente_nombre?: string
  habitacion?: string
  dieta_nombre?: string
  tiempo_nombre?: string
  estado: string
  notas_paciente?: string
  lineas: Array<{
    id: number
    plato_nombre?: string
    cantidad?: string
    excluido_paciente: boolean
    menu_opcion_id?: number | null
    menu_opcion_nombre?: string | null
    // QA 5-jul: bloqueo por alergia del paciente (cruce por grupo). El
    // cocinero DEBE verlo para no montar el plato en la bandeja.
    conflicto_alergia?: boolean
    conflicto_alergia_alimentos?: string[]
  }>
  created_at: string
  updated_at?: string | null
}

function formatoTranscurrido(iso?: string | null): string {
  if (!iso) return '—'
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (min < 1) return '< 1 min'
  if (min < 60) return `${min} min`
  return `${Math.floor(min / 60)}h ${min % 60}m`
}

export default function CocinaKDS() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const [cola, setCola] = useState<ComidaCola[]>([])
  const [loading, setLoading] = useState(true)
  // Tanda 6: doble-tap = doble transición (clase BUG-4), y los errores se
  // tragaban en silencio. busyId bloquea el botón de esa bandeja durante la
  // petición y muestra toast si falla.
  const [busyId, setBusyId] = useState<number | null>(null)

  async function avanzar(id: number, fn: (id: number) => Promise<unknown>) {
    if (busyId !== null) return
    setBusyId(id)
    try {
      await fn(id)
      await refresh()
    } catch (err: any) {
      showToast(`No se pudo actualizar la bandeja: ${err?.response?.data?.detail || err?.message || err}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  async function refresh() {
    try {
      setCola(await cocinaApi.cola())
    } catch (err: any) {
      showToast(`Error al cargar cola: ${err.message ?? err}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const i = setInterval(refresh, 10_000) // KDS refresca cada 10s
    return () => clearInterval(i)
  }, [])

  // Re-render cada 30s para que el cronometro avance sin esperar refresh()
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  // Urgencia por minutos en el estado actual (solo display)
  function urgencia(iso?: string | null): { borde: string; reloj: string } {
    const min = iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 60_000) : 0
    if (min >= 45) return { borde: '#FF5C7A', reloj: '#FF5C7A' }
    if (min >= 20) return { borde: '#F5A623', reloj: '#F5A623' }
    return { borde: '#006EB4', reloj: '#94A0CD' }
  }

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Cocina — KDS
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo} · se actualiza cada 10 s</p>
        </div>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>

      <main className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <p className="text-[#94A0CD]">Cargando...</p>}
        {!loading && cola.length === 0 && (
          <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
            <p className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
              Sin órdenes en cola
            </p>
            <p className="text-sm text-gray-500">
              Cuando el auxiliar confirme la oferta de un paciente, la orden entra aquí sola.
            </p>
          </div>
        )}

        {cola.map((c) => {
          const lineasActivas = c.lineas.filter((l) => !l.excluido_paciente)
          const opcionElegida = lineasActivas.find((l) => l.menu_opcion_nombre)?.menu_opcion_nombre
          const u = urgencia(c.updated_at ?? c.created_at)
          return (
          <div
            key={c.id}
            className="bg-white rounded-lg shadow p-4"
            style={{ borderLeft: `5px solid ${u.borde}` }}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <p className="font-bold text-2xl" style={{ fontFamily: 'JetBrains Mono', color: '#7FD4FF' }}>
                  {c.habitacion}
                </p>
                <p className="text-sm truncate">{c.cliente_nombre}</p>
                <p className="text-sm font-semibold" style={{ color: '#94A0CD' }}>
                  {c.dieta_nombre} · {c.tiempo_nombre}
                </p>
              </div>
              <div className="text-right text-xs leading-tight shrink-0" style={{ color: u.reloj, fontFamily: 'JetBrains Mono' }}>
                <div>recibido {formatoTranscurrido(c.created_at)}</div>
                <div className="font-bold">{c.estado.replace('_', ' ').toLowerCase()} {formatoTranscurrido(c.updated_at)}</div>
              </div>
            </div>

            {opcionElegida && (
              <div className="mt-2 inline-block text-xs font-semibold px-2 py-1 rounded-full"
                style={{ background: 'rgba(0,110,180,.18)', color: '#7FD4FF', border: '1px solid rgba(0,110,180,.5)' }}>
                🍽 Menú: {opcionElegida}
              </div>
            )}

            {c.notas_paciente && (
              <div className="mt-2 p-2 text-sm rounded"
                style={{ background: 'rgba(245,166,35,.14)', borderLeft: '3px solid #F5A623', color: '#F5A623' }}>
                📝 {c.notas_paciente}
              </div>
            )}

            <ul className="mt-3 space-y-1.5 text-base leading-snug">
              {lineasActivas.map((l) => (
                <li key={l.id} className={l.conflicto_alergia ? 'rounded px-2 py-1' : ''}
                    style={l.conflicto_alergia ? { background: 'rgba(255,92,122,.14)', border: '1px solid rgba(255,92,122,.5)' } : undefined}>
                  <span style={{ fontFamily: 'JetBrains Mono', color: '#94A0CD' }}>{l.cantidad ? `${l.cantidad} ` : ''}</span>
                  <span style={l.conflicto_alergia ? { textDecoration: 'line-through', color: '#94A0CD' } : undefined}>{l.plato_nombre}</span>
                  {l.conflicto_alergia && (
                    <span className="ml-2 text-xs font-bold" style={{ color: '#FF5C7A' }}>
                      ⚠ NO PREPARAR — alergia{l.conflicto_alergia_alimentos?.length ? ` (${l.conflicto_alergia_alimentos.join(', ')})` : ''}
                    </span>
                  )}
                </li>
              ))}
              {c.lineas.length > 0 && lineasActivas.length === 0 && (
                <li className="text-gray-400 italic text-sm">(sin platos por preparar)</li>
              )}
            </ul>

            <div className="mt-4 flex gap-2">
              {c.estado === 'OFERTA_CONFIRMADA' && (
                <button
                  onClick={() => avanzar(c.id, cocinaApi.enPreparacion)}
                  disabled={busyId === c.id}
                  className="px-4 rounded text-sm font-bold disabled:opacity-50"
                  style={{ minHeight: 44, borderRadius: 14, background: 'linear-gradient(120deg,#F5A623,#c47d0e)', color: '#1E1E3C', boxShadow: '0 10px 22px -8px rgba(245,166,35,.5)' }}
                >
                  {busyId === c.id ? '…' : '▶ En preparación'}
                </button>
              )}
              {c.estado === 'EN_PREPARACION' && (
                <button
                  onClick={() => avanzar(c.id, cocinaApi.marcarLista)}
                  disabled={busyId === c.id}
                  className="px-4 rounded text-sm font-bold text-white disabled:opacity-50"
                  style={{ minHeight: 44, borderRadius: 14, background: 'linear-gradient(120deg,#1d9e66,#14684a)', boxShadow: '0 10px 22px -8px rgba(47,224,138,.4)' }}
                >
                  {busyId === c.id ? '…' : '✓ Dieta lista'}
                </button>
              )}
              {c.estado === 'LISTA' && (
                <span className="font-semibold text-sm self-center" style={{ color: '#2FE08A' }}>
                  ✓ Lista — esperando recogida del auxiliar
                </span>
              )}
            </div>
          </div>
          )
        })}
      </main>
    </div>
  )
}
